import {
  rateBuilderSchema,
  RateBuilderTreeFormFields,
  rateItemSchema,
} from "@sinch/components/application/RatelistBuilder/ratelistBuilderSchema";
import { treeToRatelistNode } from "@sinch/components/application/RatelistBuilder/RateTree/utils";
import type {History, RateBuilderTreeViewItem, RateTreeState} from "@sinch/components/application/RatelistBuilder/types";
import type { RatelistType } from "@sinch/core/entities/serverEnums.types";
import {FullRatelistNodeFragment} from "@sinch/core/entities/fragments.types";
import {CalculatorRatelistInput, Ratelist, RatelistNodeInput} from "@sinch/types/sinch.types";
import {uuid} from "@sinch/utils/crypto";
import {getTreeIds, getTreeLeafsIds} from "@sinch/components/application/RatelistBuilder/utils/treeUtils";
import {difference, mergeDeepRight, pathEq} from "ramda";
import {isNotNil} from "ramda-adjunct";

export type RateBuilderHistory = History<RateTreeState<RateBuilderTreeViewItem, RateBuilderTreeFormFields>>;

const emptyBuilderState: RateBuilderHistory = {
  past: [],
  current: {
    tree: [],
    data: { Rate: {} },
    expanded: [],
    changedFields: [],
  },
  future: [],
};

/**
 * Builds the initial history state of the rate builder from ratelist data loaded from the server.
 *
 * @param ratelistData - Nodes and root node id of the ratelist, nullish for an empty builder
 * @param options - `expandAll` starts the builder with every branch of the tree expanded
 * @returns History state with the tree and the form values prefilled from the ratelist
 */
export const getBuilderDefaults = (
  ratelistData: Pick<Ratelist, "nodes" | "rootNodeId"> | null | undefined,
  options?: { expandAll?: boolean }
): RateBuilderHistory => {
  if (!ratelistData) {
    return emptyBuilderState;
  }

  const tree = getTreeValues(ratelistData);

  return {
    past: [],
    current: {
      tree,
      data: mergeDeepRight(rateBuilderSchema().getDefault(), {
        Rate: getFormValues(ratelistData).Rate,
      }) as RateBuilderTreeFormFields,
      // Leafs are filtered out, keeping them expanded would only make the collapse all control appear active.
      expanded: options?.expandAll ? difference(getTreeIds(tree), getTreeLeafsIds(tree)) : [],
      changedFields: [],
    },
    future: [],
  };
};

/**
 * Ratelist type and the ratelist-level rounding overrides an unsaved calculator preview carries, so the
 * server resolves the effective rounding of its rules the same way it does for a saved ratelist: rule
 * override, then this scope's rounding, then the instance default of `type`.
 */
export interface CalculatorRoundingScope {
  type: RatelistType;
  roundingId?: string | null;
  distanceRoundingId?: string | null;
}

/**
 * Maps the builder tree together with its form values on the ratelist structure accepted by the
 * calculator and rate composition queries. Optional structures that were never filled in are sent as
 * `null` so the server does not receive half built conditions or rates.
 *
 * @param values - Current form values of the rate builder
 * @param tree - Current builder tree, its nodes are matched against the form values by id
 * @param rootNodeId - Id of the generated root node, pass a stable one to keep server side caching
 * @param scope - Ratelist type and rounding overrides, omitted when the caller does not need rounding
 *   resolution (e.g. it only reads back the built node list)
 * @returns Ratelist structure that can be sent as an unsaved ratelist
 */
export const buildCalculatorRatelist = (
  values: RateBuilderTreeFormFields,
  tree: RateBuilderTreeViewItem[],
  rootNodeId: string = uuid(),
  scope?: CalculatorRoundingScope
): CalculatorRatelistInput => {
  const nodes = treeToRatelistNode(tree).map((node) => {
    const formValues = { ...values.Rate[node.id] };

    if (pathEq(["condition", "category"], null, formValues)) {
      formValues.condition = null;
    }

    if (pathEq(["rate", "unit", "type"], null, formValues)) {
      formValues.rate = null;
    }

    return { ...formValues, ...node } as RatelistNodeInput;
  });

  return {
    nodes: [buildRootNode(rootNodeId, tree), ...nodes],
    rootNodeId,
    ...(scope
      ? { type: scope.type, roundingId: scope.roundingId ?? null, distanceRoundingId: scope.distanceRoundingId ?? null }
      : {}),
  };
};

/**
 * Root node only references the top level rate rows, it carries no condition or rate of its own.
 *
 * @param rootNodeId - Id the generated root node gets
 * @param tree - Builder tree whose top level rows the root node points at
 */
export const buildRootNode = (rootNodeId: string, tree: RateBuilderTreeViewItem[]): RatelistNodeInput => ({
  id: rootNodeId,
  conditionSuccessNodes: tree.map((treeNode) =>
    treeNode.type === "wrapper" ? (treeNode.children?.[0]?.id ?? treeNode.id) : treeNode.id
  ),
  conditionFailureNode: null,
  condition: null,
  rate: null,
  name: null,
  description: null,
});

/**
 * Generates tree structure from query data
 *
 * @param resultData - The query result containing rate list data
 * @returns Array of tree view items representing the rate structure
 */
export const getTreeValues = (resultData: Pick<Ratelist, "nodes" | "rootNodeId">): RateBuilderTreeViewItem[] => {
  const nodes = resultData.nodes ?? [];
  const rootNode = nodes.find((node) => node.id === resultData.rootNodeId);
  const conditionSuccessNodes = rootNode?.conditionSuccessNodes ?? [];
  return makeTree(nodes as FullRatelistNodeFragment[], conditionSuccessNodes);
};
/**
 * Converts query result to form values structure
 *
 * @param resultData - The query result containing rate list data
 * @returns Form fields object with rate items mapped by ID
 */
export const getFormValues = (resultData: Pick<Ratelist, "nodes" | "rootNodeId">) => {
  const nodes = (resultData.nodes ?? []).filter((node) => node.id !== resultData.rootNodeId);

  return {
    Rate: Object.fromEntries(nodes.map((node) => [node.id, { ...rateItemSchema(true).getDefault(), ...node }])),
  };
};
/**
 * Recursively builds a tree structure from node IDs got from server
 *
 * @param nodes - Array of rate list nodes
 * @param nodeIds - Array of node IDs to process
 * @param ignoreWrapper - Flag to ignore wrapper nodes (for if-else condition nodes)
 */
const makeTree = (
  nodes: FullRatelistNodeFragment[],
  nodeIds: string[],
  ignoreWrapper = false
): RateBuilderTreeViewItem[] =>
  nodeIds.reduce((result, nodeId) => {
    const rateRow = nodes.find((node) => node.id === nodeId);
    if (!rateRow) {
      return result;
    }

    // Handle wrapper case (when condition failure node exists and we're not ignoring wrappers)
    if (!ignoreWrapper && isNotNil(rateRow?.conditionFailureNode)) {
      const wrapperItem: RateBuilderTreeViewItem = {
        id: uuid(),
        label: uuid(),
        type: "wrapper",
        children: [
          {
            id: rateRow.id,
            label: rateRow.id,
            children: makeTree(nodes, rateRow.conditionSuccessNodes ?? []),
          },
          ...makeTree(nodes, rateRow.conditionFailureNode ? [rateRow.conditionFailureNode] : [], true),
        ],
      };
      return [...result, wrapperItem];
    }

    // Handle regular case
    const regularItem: RateBuilderTreeViewItem = {
      id: rateRow.id,
      label: rateRow.id,
      children: makeTree(nodes, rateRow.conditionSuccessNodes ?? []),
    };

    // Add the regular item and its failure nodes
    return [
      ...result,
      regularItem,
      ...makeTree(nodes, rateRow.conditionFailureNode ? [rateRow.conditionFailureNode] : [], true),
    ];
  }, [] as RateBuilderTreeViewItem[]);
